import { defineStore } from 'pinia';
import fPhoneFormat from '@js/app/utils/phone-format';
import fSortArray from '@js/app/utils/sorting';
import fSortArrayDate from '@js/app/utils/sorting-date';
import routes from '@js/app/router/router';
import { firebaseConfig, vapidKey } from '@js/pwa/firebase-config';
import {
    buildFetchDataPostResult,
    buildFetchDataPostTransportFailure,
} from './http-response';

export const useAppStore = defineStore('app', {
    state: () => ({
        user: {
            auth: false,
            token: null,
            data: null,
        },
        vk: {
            script: false,
            app_id: ENV.APP_VK_APP_ID,
            data: null,
            error: '',
        },
        api: {
            url: '',
            url_pay: '',
            assets: '',
            auth: {
                get_token: '/api/v1/auth/token',
                registration: '/api/v1/auth/registration',
                phone_confirmation: '/api/v1/auth/phone-confirmation',
                authorization: '/api/v1/auth/authorization',
                authentication: '/api/v1/auth/authentication',
                email_verification: '/api/v1/auth/email-verification',
                activation: '/api/v1/auth/activation',
                reset_password_by_email: '/api/v1/auth/reset-password-by-email',
                reset_password_by_phone: '/api/v1/auth/reset-password-by-phone',
                reset_password: '/api/v1/auth/reset-password',
                sms_send: '/api/v1/auth/sms-send',
                vk: '/api/v1/auth/vk',
            },
            reset_password: {
                by_email: '/api/v1/reset-password/by-email',
                by_phone: '/api/v1/reset-password/by-phone',
                by_phone_send_sms: '/api/v1/reset-password/by-phone/send-sms',
                by_phone_check_code:
                    '/api/v1/reset-password/by-phone/check-code',
                reset_password: '/api/v1/reset-password',
            },
            user: {
                get_data: '/api/v1/user',
                update_email: '/api/v1/user/update/email',
                email_confirmation: '/api/v1/user/email-confirmation',
                email_confirm: '/api/v1/user/email-confirm',
                update_phone: '/api/v1/user/update/phone',
                update_photo: '/api/v1/user/update/photo',
                phone_confirmation: '/api/v1/user/phone-confirmation',
                sms_send: '/api/v1/user/sms-send',
                notifications: '/api/v1/user/notifications',
                notification_fill: '/api/v1/user/notification-fill',
                update_password: '/api/v1/user/update/password',
                update_description: '/api/v1/user/update/description',
                update_position: '/api/v1/user/update/position',
                vk_add: '/api/v1/user/vk/add',
                vk_remove: '/api/v1/user/vk/remove',
                exit: '/api/v1/user/exit',
                telegram_add: '/api/v1/user/telegram/add',
                firebase_add_token: '/api/v1/user/firebase/add-token',
                firebase_delete_token: '/api/v1/user/firebase/delete-token',
                firebase_message_demo: '/api/v1/demo/firebase-message',
                change_privacy_contacts:
                    '/api/v1/user/change-privacy-contacts/',
                last_activity: '/api/v1/user/last-activity/',
                data: '/api/v1/user/data/',
            },
            classifiers: {
                options: '/api/v1/classifiers/options',
                all_options: '/api/v1/classifiers/all-options',
                study_year: '/api/v1/classifiers/study-year',
                disciplines_by_student:
                    '/api/v1/classifiers/disciplines-by-student',
                disciplines_by_teacher:
                    '/api/v1/classifiers/disciplines-by-teacher',
                groups_by_teacher: '/api/v1/classifiers/groups-by-teacher',
                teachers: '/api/v1/classifiers/teachers',
                teachers_all: '/api/v1/classifiers/get-all-teachers',
                groups: '/api/v1/classifiers/groups',
                classmates: '/api/v1/classifiers/classmates',
                get_all_students: '/api/v1/classifiers/get-all-students',
                disciplines: '/api/v1/classifiers/disciplines',
                study_materials: {
                    disciplines_for_student:
                        '/api/v1/classifiers/study-materials/disciplines-for-student',
                    teachers_for_student:
                        '/api/v1/classifiers/study-materials/teachers-for-student',
                    disciplines_for_teacher:
                        '/api/v1/classifiers/study-materials/disciplines-for-teacher',
                    groups_for_teacher:
                        '/api/v1/classifiers/study-materials/groups-for-teacher',
                },
                schedule: {
                    for_student: '/api/v1/classifiers/schedule/for-student',
                    for_teacher: '/api/v1/classifiers/schedule/for-teacher',
                    all: '/api/v1/classifiers/schedule',
                },
                works: {
                    categories: '/api/v1/student-work-categories',
                },
            },
            study_materials: {
                store: '/api/v1/study-materials/store',
                select_for_student:
                    '/api/v1/study-materials/select-for-student',
                select_for_teacher:
                    '/api/v1/study-materials/select-for-teacher',
                sorting: '/api/v1/study-materials/sorting',
                update_status: '/api/v1/study-materials/update-status',
                delete: '/api/v1/study-materials/delete',
                update: '/api/v1/study-materials/update',
                file_update_view: '/api/v1/study-materials/file-update-view/',
                update_view: '/api/v1/study-materials/update-view/',
            },
            schedule: {
                lessons_by_schedule_and_employee:
                    '/api/v1/schedule/lessons-by-schedule-and-employee',
                lessons_by_employee: '/api/v1/schedule/lessons-by-employee',
                lessons_by_schedule_and_group:
                    '/api/v1/schedule/lessons-by-schedule-and-group',
                lessons_by_group: '/api/v1/schedule/lessons-by-group',
                teachers: '/api/v1/schedule/teachers',
            },
            payments: {
                get: '/api/v1/payments/get',
                get_sum_peny: '/api/v1/payments/get-sum-peny',
                make: '/api/v1/payments/make',
                success: '/api/v1/payments/success',
                failed: '/api/v1/payments/failed',
            },
            attendance: {
                update: '/api/v1/attendance/update',
                for_student: '/api/v1/attendance',
                update_for_teacher: '/api/v1/attendance/update-for-teacher',
                for_teacher: '/api/v1/attendance/for-teacher',
            },
            progression: {
                for_student: '/api/v1/progression',
                for_teacher: '/api/v1/progression/for-teacher',
            },
            library: {
                auth: '/api/v1/library/auth',
                registration: '/api/v1/library/registration',
                search: '/api/v1/library/search',
            },
            notifications: {
                class_leader_send: '/api/v1/notifications/class-leader-send',
                get_all: '/api/v1/notifications/get-all',
                get_by_author: '/api/v1/notifications/get-by-author',
                get: '/api/v1/notifications/get',
                mark_read: '/api/v1/notifications/mark-read',
                get_unread: '/api/v1/notifications/get-unread',
                info: '/api/v1/notifications/get-info-for-class-leader',
            },
            admin: {
                get_token_by_user: '/api/v1/admin/get-token',
            },
            open: {
                schedule: {
                    groups: '/api/v1/open/schedule/groups',
                    get_by_groups: '/api/v1/open/schedule/get-by-group/',
                },
            },
            student_work: {
                add: '/api/v1/student-work/add',
                update: '/api/v1/student-work/',
                delete: '/api/v1/student-work/',
                list_for_students: '/api/v1/student-work',
                list_for_teacher: '/api/v1/student-work/list-for-teacher',
                mark: '/api/v1/student-work/mark/',
                return: '/api/v1/student-work/return/',
                view_count: '/api/v1/student-work/view-count/',
            },
            demo: {
                chat: '/api/v1/demo/chat/message',
            },
        },
        classifiers: {
            users: {},
            study_materials: {},
            schedule: {},
            payments: {},
            progression: {},
            notifications: {},
            shedule_params: {
                time: [
                    {
                        start: '09:00',
                        end: '10:30',
                    },
                    {
                        start: '10:35',
                        end: '12:05',
                    },
                    {
                        start: '12:15',
                        end: '13:45',
                    },
                    {
                        start: '14:10',
                        end: '15:40',
                    },
                    {
                        start: '15:45',
                        end: '17:15',
                    },
                    {
                        start: '17:20',
                        end: '18:50',
                    },

                    {
                        start: '18:55',
                        end: '21:10',
                    },
                ],
                day: [
                    {
                        id: 1,
                        name: 'ПН',
                        long: 'Понедельник',
                    },
                    {
                        id: 2,
                        name: 'ВТ',
                        long: 'Вторник',
                    },
                    {
                        id: 3,
                        name: 'СР',
                        long: 'Среда',
                    },
                    {
                        id: 4,
                        name: 'ЧТ',
                        long: 'Четверг',
                    },
                    {
                        id: 5,
                        name: 'ПТ',
                        long: 'Пятница',
                    },
                    {
                        id: 6,
                        name: 'СБ',
                        long: 'Суббота',
                    },
                ],
            },
            works: {},
            mark: [
                {
                    id: 1,
                    name: 'Зачтено',
                    color: '#D9F69B',
                },
                {
                    id: 2,
                    name: 'Незачтено',
                    color: '#F8ABB9',
                },
                {
                    id: 3,
                    name: 'Неудовлетворительно',
                    color: '#F8ABB9',
                },
                {
                    id: 4,
                    name: 'Удовлетворительно',
                    color: '#FFCC00',
                },
                {
                    id: 5,
                    name: 'Хорошо',
                    color: '#F6C79B',
                },
                {
                    id: 6,
                    name: 'Отлично',
                    color: '#D9F69B',
                },
                {
                    id: 99,
                    name: 'Сдано',
                    color: '#E5F2FF',
                },
                {
                    id: 100,
                    name: 'Неявка',
                    color: '',
                },
            ],
            markWork: [
                {
                    id: 6,
                    name: 'Отлично',
                    color: '#D9F69B',
                },
                {
                    id: 5,
                    name: 'Хорошо',
                    color: '#F6C79B',
                },
                {
                    id: 4,
                    name: 'Удовлетворительно',
                    color: '#FFCC00',
                },
                {
                    id: 3,
                    name: 'Неудовлетворительно',
                    color: '#F8ABB9',
                },
            ],
        },
        helpers: {
            materials: {
                reload: 1,
                deleteId: null,
                editData: null,
            },
            works: {
                reload: 1,
                deleteId: null,
                editData: null,
                editMarkData: null,
                editMarkReturn: false,
            },
            modals: {
                modalProfileId: null,
                modalProfileData: null,
            },
            telegram: {
                reload: 1,
                deleteId: null,
                editData: null,
            },
            notifications: {
                unread: 0,
                pushed: [],
            },
            pwa: {
                message: false,
                installed: true,
            },
            enter: {
                phone: null,
            },
        },
    }),
    getters: {
        getNoPhoto: (state) => `${state.api.assets}images/no-avatar.svg`,
        isTeacher: (state) => state.user.data?.roles.includes('teacher'),
        isLeader: (state) =>
            state.user.data?.user_data?.pin
                ? state.user.data?.user_data?.leader_id ===
                  state.user.data?.user_data?.pin
                : false,
        getUserFIO: (state) =>
            `${state.user.data?.user_data.family} ${state.user.data?.user_data.name} ${state.user.data?.user_data.patronymic}`,
    },
    actions: {
        async fetchDataPost(
            apiCode: string,
            fetchData = {},
            method = 'POST',
            sortCode = null,
            asc = false,
            addStoreCode = null,
            addStoreItem = {},
            restoreData = false,
            apiUrlAdd = ''
        ): Promise<any> {
            try {
                if (addStoreCode && !restoreData) {
                    if (addStoreItem[addStoreCode]) {
                        return {
                            data: addStoreItem[addStoreCode],
                        };
                    }
                }
                const url = `${this.api.url}${apiCode}${apiUrlAdd}`;
                const isFormData = fetchData instanceof FormData;
                let fetchError = false;
                const headers = {} as any;
                if (!isFormData) {
                    headers['Content-Type'] = 'application/json';
                    headers.Accept = 'application/json';
                } else {
                    headers.Accept = 'application/json';
                }
                if (this.user.token) {
                    headers.Authorization = `Bearer ${this.user.token}`;
                }
                const response = await fetch(url, {
                    method,
                    headers,
                    body: isFormData ? fetchData : JSON.stringify(fetchData),
                });
                const data = await response
                    .clone()
                    .json()
                    .catch(() => {
                        response.text();
                        fetchError = true;
                    });
                if (response.status === 401) {
                    this.userLogout(true);
                }
                if (response.status === 500) {
                    data.message = 'Ошибка отправки данных';
                }

                if (sortCode && data.data) {
                    data.data = fSortArrayDate(data.data, sortCode, asc);
                }

                if (addStoreCode && response.ok) {
                    addStoreItem[addStoreCode] = data.data;
                }

                return buildFetchDataPostResult(response, data, fetchError);
            } catch (error) {
                console.log('error:', error);
                return buildFetchDataPostTransportFailure();
            }
        },
        async fetchDataGet(
            apiCode: string,
            addStoreCode = null,
            addStoreItem = {},
            sortData = false,
            restoreData = false,
            asc = false,
            apiUrlAdd = ''
        ) {
            try {
                if (addStoreCode && !restoreData) {
                    if (addStoreItem[addStoreCode]) {
                        return {
                            data: addStoreItem[addStoreCode],
                        };
                    }
                }

                const url = `${this.api.url}${apiCode}${apiUrlAdd}`;
                let fetchError = false;
                const headers = {
                    'Content-Type': 'application/json',
                    'Accept': 'application/json',
                };
                if (this.user.token) {
                    headers.Authorization = `Bearer ${this.user.token}`;
                }

                const response = await fetch(url, {
                    method: 'get',
                    headers,
                });

                const data = await response
                    .clone()
                    .json()
                    .catch(() => {
                        response.text();
                        fetchError = true;
                    });
                if (response.status === 401) {
                    this.userLogout(true);
                }
                if (response.status === 500) {
                    data.message = 'Ошибка получения данных';
                }
                if (response.ok) {
                    if (sortData) {
                        if (sortData === true) {
                            data.data = fSortArray(data.data);
                        } else {
                            data.data = fSortArrayDate(
                                data.data,
                                sortData,
                                asc
                            );
                        }
                    }
                    if (addStoreCode) {
                        addStoreItem[addStoreCode] = data.data;
                    }
                }

                return {
                    error: fetchError
                        ? 'Ошибка получения данных'
                        : !response.ok
                        ? data.message
                        : '',
                    data,
                };
            } catch (error) {
                console.log('error:', error);
                return {
                    error: 'Ошибка получения данных',
                };
            }
        },
        async vkScriptAndStatus() {
            // Проверка авторизации VK
            const script = document.getElementById('script-vk');
            if (script !== null && script !== undefined) {
                await this.vkStatus();
            } else {
                const scriptVK = document.createElement('script');
                scriptVK.type = 'text/javascript';
                scriptVK.src = 'https://vk.com/js/api/openapi.js';
                scriptVK.setAttribute('id', 'script-vk');
                document.body.appendChild(scriptVK);
                scriptVK.addEventListener('load', async () => {
                    await this.vkStatus();
                });
            }
        },
        async vkStatus() {
            try {
                this.vk.error = '';
                this.vk.script = true;
                await VK.init({
                    apiId: this.vk.app_id,
                });
                await VK.Auth.getLoginStatus((response: any) => {
                    this.vk.data = response;
                    if (response.session) {
                        // Получаем данные юзера
                        VK.Api.call(
                            'users.get',
                            {
                                user_ids: this.vk.data.session.mid,
                                v: '5.199',
                            },
                            (res: any) => {
                                this.vk.data.session.user = res.response[0];
                            }
                        );
                    }
                    return response;
                });
            } catch (error) {
                console.log('vkStatus: Error VK API');
                this.vk.error = 'Error VK API';
            }
        },
        async vkLogin(vkEnter = false) {
            try {
                this.vk.error = '';
                await VK.Auth.login(async (response: any) => {
                    this.vk.data = response;
                    if (vkEnter) {
                        if (response.session) {
                            const formResult = await this.fetchDataPost(
                                this.api.auth.vk,
                                {
                                    vk_id: response.session.mid,
                                    device_name: this.getDeviceName(),
                                }
                            );
                            if (formResult.error) {
                                console.log('Error api.auth.vk');
                                this.vk.error = 'Профиль VK не привязан';
                            } else {
                                this.tokenSet(formResult.data.token);
                                this.userIsAuth();
                            }
                        } else {
                            console.log('Error api.auth.vk vkEnter');
                            this.vk.error = 'Не удалось авторизоваться';
                        }
                    } else if (response.session) {
                        const formResult = await this.fetchDataPost(
                            this.api.user.vk_add,
                            {
                                vk_id: response.session.mid,
                            }
                        );
                        if (formResult.error) {
                            console.log('Error api.user.vk_add');
                            this.vk.error = formResult.error;
                        } else {
                            this.user.data.vk_id = response.session.mid;
                        }
                    } else {
                        console.log('Error api.auth.vk');
                        this.vk.error = 'Не удалось авторизоваться';
                    }
                });
            } catch (error) {
                console.log('vkLogin: Error VK API');
                this.vk.error = 'Error VK API';
            }
        },
        async vkLogout(vkRemove = false) {
            try {
                this.vk.error = '';
                if (this.vk.data) {
                    if (this.vk.data.session) {
                        await VK.Auth.logout(async (response: any) => {
                            if (vkRemove) {
                                this.vk.data = response;
                                const formResult = await this.fetchDataPost(
                                    this.api.user.vk_remove,
                                    {
                                        vk_id: this.user.data.vk_id,
                                    },
                                    'DELETE'
                                );
                                if (formResult.error) {
                                    this.vk.error = formResult.error;
                                    console.log('Error api.user.vk_remove');
                                } else {
                                    this.user.data.vk_id = null;
                                }
                            }
                        });
                    }
                }
                return {};
            } catch (error) {
                console.log('vkLogout: Error VK API');
                this.vk.error = 'Error VK API';
                return {
                    error: 'Error VK API',
                };
            }
        },
        async firebaseDeleteToken() {
            const currentToken = localStorage.getItem('firebaseToken');
            if (currentToken) {
                const formResult = await this.fetchDataPost(
                    this.api.user.firebase_delete_token,
                    {
                        fcm_token: currentToken,
                    }
                );
                if (!formResult.error) {
                    const app = firebase.initializeApp(firebaseConfig);
                    const messaging = firebase.messaging();
                    messaging
                        .deleteToken(currentToken)
                        .then(async () => {
                            localStorage.removeItem('firebaseToken');
                        })
                        .catch((error) => {
                            console.log('firebaseDeleteToken(): ', error);
                        });
                } else {
                    console.log(
                        'api.user.firebase_delete_token error',
                        formResult.error
                    );
                }
            }
        },
        async userLogout(sessionEnd: boolean) {
            if (!sessionEnd) {
                await this.firebaseDeleteToken();
                await this.fetchDataGet(this.api.user.exit);
            }
            // await this.vkLogout();
            await this.tokenRemove();
            this.user.auth = false;
            this.user.data = null;
            // this.vk.data = null;
        },
        async userIsAuth() {
            try {
                this.tokenGet();
                this.helpers.formError = '';
                let formResult = await this.fetchDataGet(
                    this.api.user.get_data
                );

                await this.vkScriptAndStatus();

                if (formResult.error) {
                    console.log('Ошибка проверки авторизации');
                    return {
                        error: formResult.error,
                    };
                }

                this.user.data = formResult.data.data;
                if (this.isLeader) {
                    this.user.data.roles.push('leader');
                }

                this.user.auth = true;

                // this.user.data.ipr_book_id = null;

                if (!this.user.data.thumb) {
                    this.user.data.thumb = this.getNoPhoto;
                }

                formResult = await this.fetchDataGet(
                    this.api.classifiers.all_options,
                    'all_options',
                    this.classifiers
                );
                if (formResult.error) {
                    console.log('Ошибка получения данных');
                    return {
                        error: 'Ошибка получения данных',
                    };
                }

                return {};
            } catch (error) {
                console.log('Ошибка проверки авторизации');
                return {
                    error: 'Ошибка проверки авторизации',
                };
            }
        },
        tokenSet(token: any) {
            this.tokenRemove();
            this.user.token = token;
            localStorage.setItem('token', token);
        },
        tokenGet() {
            const token = localStorage.getItem('token');
            this.user.token = token;
        },
        tokenRemove() {
            localStorage.removeItem('token');
            this.user.token = null;
        },
        phoneFormat(phone: any) {
            return fPhoneFormat(phone);
        },
        getTeacherById(id: any) {
            let res = null;
            if (this.classifiers.teachers) {
                res = this.classifiers.teachers.find(
                    (item: any) => String(item.id) === String(id)
                );
            }
            if (!res && this.classifiers.teachers_all) {
                res = this.classifiers.teachers_all.find(
                    (item: any) =>
                        String(item.id) === String(id) ||
                        String(item.external_teacher_id) === String(id)
                );
            }
            return res;
        },
        getClassmatesById(id: any) {
            if (this.classifiers.classmates) {
                return this.classifiers.classmates.find(
                    (item: any) => String(item.id) === String(id)
                );
            }
        },
        async getUserById(id: any) {
            if (this.classifiers.users[id]) {
                return this.classifiers.users[id];
            }

            const formResult = await this.fetchDataGet(this.api.user.data + id);
            if (formResult.data) {
                formResult.data.data.user_data.id = id;
                formResult.data.data.user_data.thumb =
                    formResult.data.data.thumb;
                this.classifiers.users[id] = formResult.data.data.user_data;
                return this.classifiers.users[id];
            }

            return false;
        },
        getGroupById(id: any) {
            if (this.classifiers.groups) {
                return this.classifiers.groups.find(
                    (item: any) => String(item.id) === String(id)
                );
            }
        },
        getRouteByID(id: any) {
            return routes.find((item) => item.id === id);
        },
        showPage(route: any) {
            if (this.user.data) {
                let res = route.meta.roles
                    ? this.user.data.roles.includes(route.meta.roles)
                    : true;
                if (route.meta.rolesBan) {
                    route.meta.rolesBan.forEach((role: any) => {
                        if (this.user.data.roles.includes(role)) {
                            res = false;
                        }
                    });
                }
                return res;
            }
            return false;
        },
        isStudentBanned() {
            let res = {
                banned: false,
                showInfo: true,
                text: '',
            };
            if (this.user.data) {
                if (this.user.data.roles.includes('student')) {
                    if (this.user.data.roles.includes('expelled_student')) {
                        res = {
                            banned: true,
                            showInfo: false,
                            text: 'Вы отчислены, функционал личного кабинета ограничен.',
                        };
                    }
                    else if (
                        this.user.data.roles.includes(
                            'academic_vacation_student'
                        )
                    ) {
                        res = {
                            banned: true,
                            showInfo: true,
                            text: 'Вы в академическом отпуске, функционал личного кабинета ограничен.',
                        };
                    } else if (this.user.data.user_data.reason) {
                        res = {
                            banned: true,
                            showInfo: true,
                            text: this.user.data.user_data.reason,
                        };
                    }
                }
            }
            return res;
        },
        getDeviceName() {
            const navigatorInfo = window.navigator;
            const screenInfo = window.screen;
            let uid = navigatorInfo.mimeTypes.length as any;
            uid += navigatorInfo.userAgent.replace(/\D+/g, '');
            uid += navigatorInfo.plugins.length;
            uid += screenInfo.height || '';
            uid += screenInfo.width || '';
            uid += screenInfo.pixelDepth || '';
            return uid;
        },
        getMarkColor(id = '') {
            const res = this.classifiers.mark.find((item) => {
                return String(item.id) === String(id);
            });
            return res ? res.color : '';
        },
        getMarkName(id = '') {
            const res = this.classifiers.mark.find((item) => {
                return String(item.id) === String(id);
            });
            return res ? res.name : '';
        },
    },
});
